import {
  GetCampaignsQuery,
  GetCampaignsQuery_campaigns_Campaign,
  GetNewslettersQuery,
  GetNewslettersQuery_newsletters_Newsletter,
} from '__generated__/queries-newsletter';
import { Moment } from 'moment';

const getStartOfTheCalendarMonthDate = (date: Moment) =>
  date.clone().startOf('month').subtract(6, 'day');

const getEndOfTheCalendarMonthDate = (date: Moment) =>
  date.clone().endOf('month').add(6, 'day');

const getDaysBetweenTwoDates = (dateA: Moment, dateB: Moment): Moment[] => {
  const d = dateA.clone();
  const result: Moment[] = [];
  // eslint-disable-next-line fp/no-loops
  while (d.isSameOrBefore(dateB)) {
    // eslint-disable-next-line fp/no-mutating-methods
    result.push(d.clone());
    d.add(1, 'days');
  }
  return result;
};

export const getDaysInTheCalendarMonth = (date: Moment) =>
  getDaysBetweenTwoDates(
    getStartOfTheCalendarMonthDate(date),
    getEndOfTheCalendarMonthDate(date),
  );

export interface CalendarEvent {
  id: string;
  title?: string;
  allDay: boolean;
  start: string | null | undefined;
  end: string | null | undefined;
  resource: {
    type: 'newsletter' | 'campaign';
    brandKey?: string;
    tooltip?: string;
    auto: boolean;
  };
}

const buildEventFromNewsletter = (
  newsletter: GetNewslettersQuery_newsletters_Newsletter,
  date: Moment,
): CalendarEvent => {
  const time = newsletter.automationTime;
  const tooltip = time
    ? `Création automatique programmée à ${Math.floor(time / 100)}h${
        time % 100
      }`
    : undefined;
  return {
    id: newsletter.id,
    title: newsletter.name,
    allDay: true,
    start: date.toString(),
    end: date.toString(),
    resource: {
      type: 'newsletter',
      brandKey: newsletter.brandKey,
      tooltip,
      auto: !!newsletter.automationTime,
    },
  };
};

const buildEventFromCampaign = (
  campaign: GetCampaignsQuery_campaigns_Campaign,
): CalendarEvent => ({
  id: campaign.id,
  title: campaign.newsletter?.name,
  allDay: true,
  start: campaign.availableAt,
  end: campaign.availableAt,
  resource: {
    type: 'campaign',
    brandKey: campaign.newsletter?.brandKey,
    tooltip: campaign.subject,
    auto: !!campaign.newsletter?.automationTime,
  },
});

const getEventsFromNewsletter = (
  viewedDate: Moment,
  newsletter: GetNewslettersQuery_newsletters_Newsletter,
  campaigns: GetCampaignsQuery_campaigns_Campaign[],
) => {
  return getDaysInTheCalendarMonth(viewedDate)
    .filter((day) => {
      const campaignsScheduledForThisDay = campaigns.filter(
        (campaign) => day.format('YYYY-MM-DD') === campaign.availableAt,
      );

      return !campaignsScheduledForThisDay.some(
        (campaign) => campaign.newsletter?.id === newsletter.id,
      );
    })
    .map((day) => buildEventFromNewsletter(newsletter, day));
};

export const buildEvents = (
  viewedDate: Moment,
  campaignData?: GetCampaignsQuery,
  newsletterData?: GetNewslettersQuery,
): CalendarEvent[] => {
  if (
    !campaignData ||
    !campaignData.campaigns ||
    !newsletterData ||
    !newsletterData.newsletters
  ) {
    return [];
  }
  return [
    ...campaignData.campaigns.map(buildEventFromCampaign),
    ...newsletterData.newsletters.reduce(
      (prev, newsletter) => [
        ...prev,
        ...getEventsFromNewsletter(
          viewedDate,
          newsletter,
          campaignData.campaigns,
        ),
      ],
      [] as CalendarEvent[],
    ),
  ];
};
